import React, { useEffect, useRef } from 'react';
import { ADSENSE_CLIENT_ID, ADSENSE_SLOT_BANNER, ADSENSE_SLOT_RECTANGLE } from '../config/runtime';
import { useEntitlements } from '../entitlements/useEntitlements';
import './AdSlot.css';

interface AdSlotProps {
  slotId?: string;
  format?: 'banner' | 'rectangle' | 'interstitial';
  label?: string;
}

// Resolves the configured ad unit id for a given format when none is passed explicitly.
const SLOT_BY_FORMAT: Record<NonNullable<AdSlotProps['format']>, string> = {
  banner: ADSENSE_SLOT_BANNER,
  rectangle: ADSENSE_SLOT_RECTANGLE,
  interstitial: ADSENSE_SLOT_RECTANGLE,
};

// Injects the AdSense loader script once, lazily, when a client id is configured.
// Keeping it out of index.html means no third-party script loads for paid users or
// in environments where ADSENSE_CLIENT_ID is empty.
function ensureAdSenseScript(clientId: string) {
  if (typeof document === 'undefined') return;
  if (document.querySelector('script[data-adsbygoogle-loader]')) return;
  const s = document.createElement('script');
  s.async = true;
  s.src = `https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=${clientId}`;
  s.crossOrigin = 'anonymous';
  s.setAttribute('data-adsbygoogle-loader', 'true');
  document.head.appendChild(s);
}

// Renders a Google AdSense unit when ADSENSE_CLIENT_ID is configured, otherwise a
// labeled placeholder. Hidden entirely for ad-free (paid) users.
// TAG: real AdSense account + slot ids required to serve live ads.
export default function AdSlot({ slotId, format = 'banner', label = 'Advertisement' }: AdSlotProps) {
  const { adFree, loading } = useEntitlements();
  const insRef = useRef<HTMLModElement | null>(null);
  const resolvedSlot = slotId || SLOT_BY_FORMAT[format];

  useEffect(() => {
    if (adFree || !ADSENSE_CLIENT_ID || !resolvedSlot) return;
    ensureAdSenseScript(ADSENSE_CLIENT_ID);
    try {
      (window.adsbygoogle = window.adsbygoogle || []).push({});
    } catch {
      /* AdSense not ready */
    }
  }, [adFree, resolvedSlot]);

  if (loading || adFree) return null;

  if (!ADSENSE_CLIENT_ID || !resolvedSlot) {
    return (
      <div className={`ad-slot ad-slot--${format} ad-slot--placeholder`} role="complementary" aria-label={label}>
        <span className="ad-slot__tag">Ad</span>
        <span className="ad-slot__hint">{label} — remove ads by upgrading</span>
      </div>
    );
  }

  return (
    <div className={`ad-slot ad-slot--${format}`}>
      <ins
        ref={insRef}
        className="adsbygoogle"
        style={{ display: 'block', width: '100%' }}
        data-ad-client={ADSENSE_CLIENT_ID}
        data-ad-slot={resolvedSlot}
        data-ad-format="auto"
        data-full-width-responsive="true"
      />
    </div>
  );
}
